import { configureStore } from '@reduxjs/toolkit'

import rootReducer from './reducers'

/**
 * Read a value from local storage and normalise empty / 'undefined' to null.
 */
export const getItemFromLocalStorage = (key: string): string | null => {
    const value = localStorage.getItem(key)

    if (!value || value === 'undefined') {
        return null
    }

    return value
}

/**
 * Promise-based wrapper around the synchronous sessionStorage API.
 */
export const asyncSessionStorage = {
    setItem: (key: string, value: string): Promise<void> =>
        Promise.resolve().then(() => {
            sessionStorage.setItem(key, value)
        }),
    getItem: (key: string): Promise<string | null> =>
        Promise.resolve().then(() => sessionStorage.getItem(key)),
    removeItem: (key: string): Promise<void> =>
        Promise.resolve().then(() => sessionStorage.removeItem(key)),
}

export const DEFAULT_NOT_FOUNDED_ROUTE = {
    globalRoutes: false,
}

export const initialState = {
    app: {
        busy: false,
        loadingMap: true,
    },
    auth: {
        busy: true,
        user: null,
        error: null,
    },
    locale: {
        lang: 'cs',
    },
    superAdmin: {
        users: [],
        companies: [],
        locationsIP: [],
    },
    devices: {
        data: [],
        deviceWriter: [],
        deviceServiceHistory: [],
        markers: [],
    },
    deviceOilHistory: {
        dataGraph: [],
        data: [],
        from: null,
        to: null,
    },
    notFoundRoutes: {
        ...DEFAULT_NOT_FOUNDED_ROUTE,
    },
}

const appVersion = import.meta.env.VITE_APP_VERSION as string | undefined
const isDev = appVersion === '@local' || appVersion === '@dev'

const store = configureStore({
    reducer: rootReducer,
    preloadedState: initialState as never,
    devTools: isDev,
    // Legacy reducers in this app mutate state directly; RTK's strict checks
    // would crash on every dispatch. Disable both immutability and
    // serializability invariants to preserve the pre-RTK behavior.
    middleware: getDefault => getDefault({
        immutableCheck: false,
        serializableCheck: false,
    }),
})

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

export default store
